import { AsyncPipe } from '@angular/common';
import { AfterContentInit, Component, ElementRef, OnDestroy, OnInit, Signal, inject, ChangeDetectionStrategy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { Banner, Link, UmbracoLink } from 'src/app/models';
import { HomeType, ProductSection } from 'src/app/models/home';
import { AuthService } from 'src/app/services/auth';
import { BannerService } from 'src/app/services/banner.service';
import { CategoryService } from 'src/app/services/category/category.service';
import { ContentService } from 'src/app/services/content/content.service';
import { ProductQuery, ProductService } from 'src/app/services/product';
import { ThemeService } from 'src/app/services/theme/theme.service';
import { RichTextBoxComponent } from 'src/app/shared/components';
import { HeroBannerComponent } from 'src/app/shared/components/hero-banner/hero-banner.component';
import { ProductCardComponent } from 'src/app/shared/components/product/card';
import { ContentSection } from 'src/app/shared/layout/content-section';
import { ContentImagePipe } from 'src/app/shared/pipes';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-home-fancy',
  templateUrl: './home-fancy.component.html',
  styleUrls: ['./home-fancy.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [HeroBannerComponent, RouterLink, MatIconModule, AsyncPipe, ProductCardComponent, ContentImagePipe, RichTextBoxComponent],
})
export class HomeFancyComponent extends ContentSection implements OnInit, AfterContentInit, OnDestroy {
  private productQuery = inject(ProductQuery);
  private productService = inject(ProductService);
  private categoryService = inject(CategoryService);
  private authService = inject(AuthService);
  private themeService = inject(ThemeService);
  private elRef = inject(ElementRef);
  private bannerService = inject(BannerService);

  private subscription: Subscription;

  heroBanners: Signal<Banner[]>;

  primarySection: ProductSection;

  topTextarea: string;
  topTextareaImage: string;
  topTextareaLink: UmbracoLink;

  bottomTextarea: string;
  bottomTextareaImage: string;

  loading: Observable<boolean>;

  constructor() {
    const contentService = inject(ContentService);
    super(contentService, HomeType.fancy, { meta: true });

    this.topTextarea = this.content.get('topArticleText');
    this.topTextareaImage = this.content.get('topImage');
    this.topTextareaLink = this.content.get('topArticleLink') as UmbracoLink;

    this.bottomTextarea = this.content.get('bottomArticleText');
    this.bottomTextareaImage = this.content.get('bottomImage');

    this.heroBanners = toSignal(this.bannerService.getHeroBanners(this.contentService.getMerchantId()));
  }

  async ngOnInit() {
    this.subscription = new Subscription();

    const primaryProductCount = 4;

    this.loading = this.productQuery.selectLoading();
    await this.initFrontPageProducts(primaryProductCount);

    this.subscription.add(
      this.authService.member.subscribe(() => {
        this.initFrontPageProducts(primaryProductCount);
      }),
    );
  }

  ngAfterContentInit() {
    this.themeService.subscribeComponent(this.elRef);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  initFrontPageProducts(primaryProductCount: number) {
    this.categoryService
      .getFrontpage()
      .pipe(take(1))
      .subscribe(async (frontpage) => {
        const categories = [...frontpage];
        const primaryCategory = categories.shift();
        if (primaryCategory) {
          await this.productService.getFrontpageProducts(primaryProductCount, primaryCategory.id);

          const link: Link = {
            url: `${environment.urls.products}`,
            params: null,
            name: this.content.get('seeAllLocalOffers'), // TODO: how to name this?
          };
          if (!primaryCategory.hidden) {
            link.url = `${environment.urls.products}/${primaryCategory.categoryUrl}`;
          }

          const productSection: ProductSection = {
            title: this.content.get('productTitle') || primaryCategory.name,
            products: this.productQuery.selectFrontpageProducts$.pipe(switchMap((ids) => this.productQuery.selectMany(ids[0]))),
            link,
          };
          this.primarySection = productSection;
        }
      });
  }
}
